import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Collapse,
  Group,
  Modal,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { humanize } from '../../lib/enums';
import { formatDate, formatHours } from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';
import { NO_WORKING_WEEK_COSTS } from '../staff/schedule';
import { DAY_ENTRIES_PATH, DayEntryModal, describeShift, type TimesheetDayEntry } from './DayEntryEditor';
import { periodHoursLink } from './PeriodHoursGrid';
import { workingWeekLink } from './WorkingWeeksPanel';

interface TimesheetDay {
  date: string;
  scheduledMinutes: number;
  workedMinutes: number;
  breakMinutes: number;
  exceptions: string[];
}

interface Timesheet {
  id: string;
  staffId: string;
  staffName?: string | null;
  payrollPeriodId: string;
  scheduledMinutes: number;
  workedMinutes: number;
  breakMinutes: number;
  overtimeMinutes: number;
  status: string;
  approvedAt?: string | null;
  approvedByName?: string | null;
  days?: TimesheetDay[];
  exceptions?: string[];
  note?: string | null;
}

interface Period {
  id: string;
  periodStart: string;
  periodEnd: string;
}

/**
 * Exception codes become sentences here rather than on the server, so the API stays language-free
 * for the offline register client and this file is the only thing a translation would touch.
 */
const EXCEPTION_COPY: Record<string, string> = {
  missingClockOut: 'A shift was never clocked out',
  missingClockIn: 'A shift was never clocked in',
  impossibleShift: 'A shift ran impossibly long — check the punches',
  /*
   * Not a broken record — a real figure that nobody watched happen.
   *
   * A shift left open overnight used to leave a bartender with two bad choices: press Clock out and
   * lose the whole night to an impossible span, or find somebody with a laptop. The register now
   * closes the old shift at the end of its own trading day and starts tonight's in one tap, so the
   * hours below are the honest upper bound rather than a zero. Yellow, because the figure is an
   * assumption and this is the screen where somebody says whether it stands.
   */
  forcedClockOut: 'Closed automatically when the next shift started — check the hours',
  /*
   * This read "No schedule set for this period", which named the wrong thing entirely. Nothing
   * about a pay period is missing — nobody has ever said which days and hours this PERSON works,
   * and until somebody does it will say the same on every period there will ever be. An owner who
   * reads it as a property of the fortnight goes looking at the fortnight.
   */
  noSchedule: 'No working week set',
  invalidSchedule: 'Their schedule could not be read',
  workedOnDayOff: 'Worked on a scheduled day off',
  shortOfSchedule: 'Worked less than scheduled',
  overSchedule: 'Worked more than scheduled',
  longDay: 'Longer than a normal working day',
  noOrdinaryDayThreshold: 'Long days could not be checked — no working-day length is set',
  manualHours: 'Entered by hand',
  /*
   * A day from before the clock: a bare total somebody remembered, with no times behind it. It is
   * paid exactly as a punched day is, so the figures beside it need no qualification — but "about
   * eight hours that Tuesday" and a pair of real punches are not the same evidence, and only the
   * badge can say which one a row is. Read as an estimate, said as one.
   */
  manualTotal: 'Estimated — a total, with no times',
  pto: 'PTO',
  recordedLeave: 'Linked leave record',
  ptoNeedsReconciliation: 'Paid day off — reconcile vacation record',
  holidayHoursNeedReview: 'Holiday hours need a timed shift / calendar review',
  holiday_calendar_changed: 'Holiday calendar changed — rebuild these hours',
  schedule_changed: 'Working week changed — rebuild these hours',
  leave_changed: 'Leave changed — rebuild these hours',
};

const TIMESHEETS_PATH = '/admin/v1/payroll/timesheets';

/** Red means the record is broken and the number cannot be trusted; yellow means look at it. */
const BROKEN_RECORD = new Set([
  'missingClockOut',
  'missingClockIn',
  'impossibleShift',
  'noSchedule',
  'invalidSchedule',
]);

/**
 * Codes whose entire content is already in the figures printed beside them.
 *
 * "Worked less than scheduled" next to a Difference column reading −5m is the same sentence twice,
 * and the shorter one carries the size. With no grace period configured a punch is almost never
 * exact to the minute, so these fired on nearly every person in nearly every period — which made
 * the flag column noise, disabled the bulk approval permanently, and taught a manager that flags
 * mean nothing. The number stays; the badge goes.
 */
const REDUNDANT_WITH_FIGURES = new Set(['shortOfSchedule', 'overSchedule']);

/**
 * Worth seeing on the day it happened, never a reason to hold up an approval.
 *
 * Two of the staff here work eleven-hour days by agreement, twice a week, every week. Flagging that
 * roster as an anomaly on both people in every period is flagging the owner's own decision back at
 * him. A day that ran long unplanned still shows as worked-above-scheduled in the day's own figures.
 *
 * The manual codes are labels, not warnings: a hand-entered day, an estimated one or a PTO day is a
 * decision the manager already made and wrote a note for, and asking the same person to justify it
 * a second time at approval is how a reason box stops meaning anything. An estimate is quiet for
 * one further reason — the days it can be written for are the months worked before anybody was on
 * the clock, so a period full of them would hold the whole period up on a fact that is already
 * settled. The badge stays on the day, quietly, so anyone reading the sheet can tell a counted day
 * from a written one, and a written one from a remembered one.
 */
const QUIET_ON_THE_DAY = new Set(['longDay', 'manualHours', 'manualTotal', 'pto']);

/** Not about a person at all — the setting behind the check is missing. Said once, not per row. */
const CONFIGURATION_GAP = 'noOrdinaryDayThreshold';

/**
 * The one flag on this screen whose answer is not on this screen.
 *
 * Nobody has ever said which days and hours this person works, so there is nothing for their hours
 * to be measured against — and it costs three things at once, every period, for ever: their
 * overtime is worked out day by day against a schedule and so computes as zero however long the
 * days ran, the one-click Approve turns into a typed reason, and the days they were meant to work
 * have no rows on the sheet to correct. Naming it is not enough. The screen that ends it is one
 * link away, and until it was linked from here the flag was a diagnosis with nowhere to go.
 */
const NO_WORKING_WEEK = 'noSchedule';

function exceptionColor(code: string): string {
  if (BROKEN_RECORD.has(code)) return 'red';
  return QUIET_ON_THE_DAY.has(code) ? 'gray' : 'yellow';
}

/** What actually needs a manager's decision before these hours are signed off. */
function decidingCodes(codes: string[] | undefined): string[] {
  return (codes ?? []).filter(
    (code) => !REDUNDANT_WITH_FIGURES.has(code) && !QUIET_ON_THE_DAY.has(code) && code !== CONFIGURATION_GAP,
  );
}

/**
 * The missing working weeks, named, with the door to fix them.
 *
 * Everyone it affects in one sentence rather than a red badge repeated down a column: the badge can
 * only say what is wrong, and this is the one thing on the screen whose answer lives on another
 * screen. When it is one person the link opens their editor directly, because the extra step of
 * finding them again in a list is exactly the step that does not get taken.
 */
function NoWorkingWeekAlert({
  sheets,
  periodStart,
}: {
  sheets: Timesheet[];
  /**
   * The first day of the fortnight being read, carried into the editor as the date the week has to
   * count from. Without it the editor opens on today and a week set to answer this alert starts
   * after the period it was opened from ended — the period still finds no pattern, every day is
   * still flagged, overtime is still zero, and this alert is still here with the same button on it.
   */
  periodStart?: string;
}) {
  const only = sheets.length === 1 ? sheets[0] : null;
  const who = only
    ? (only.staffName ?? 'this person')
    : sheets.map((s) => s.staffName ?? 'somebody').join(', ');
  const theirs = only?.staffName ? `${only.staffName}'s` : 'their';
  return (
    <Alert
      color="red"
      variant="light"
      mb="md"
      title={
        only
          ? `${only.staffName ?? 'Somebody'} has no working week set`
          : `${sheets.length} people have no working week set`
      }
    >
      <Text size="sm">
        Nobody has said which days and hours {who} {only ? 'works' : 'work'}, so there is nothing for these
        hours to be measured against. {NO_WORKING_WEEK_COSTS}
      </Text>
      <Button
        component={Link}
        to={workingWeekLink(only ? only.staffId : null, periodStart)}
        size="compact-sm"
        mt="xs"
      >
        {only ? `Set ${theirs} working week` : 'Set their working weeks'}
      </Button>
    </Alert>
  );
}

/**
 * Where a manager confirms what people actually worked before anyone is paid for it.
 *
 * Until this existed the payroll run paid whatever the registers happened to record, and a forgotten
 * clock-out or a shift someone covered was nobody's decision. The comparison against the schedule
 * is the point: a seven-hour day and a day cut short look identical without it.
 */
export function HoursApproval() {
  const queryClient = useQueryClient();
  const [periodId, setPeriodId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  /**
   * The person whose flagged hours are being signed off, and the reason being given for it.
   *
   * Approving is the moment somebody takes responsibility for hours that do not add up — a shift
   * worked on a scheduled day off, a punch that had to be invented — and the record has always had
   * a place to keep why. Nothing wrote to it, so the register went to the accountant carrying a
   * name and a date and no answer to the only question anybody asks a fortnight later, which is
   * why these hours were allowed.
   */
  const [signOff, setSignOff] = useState<Timesheet | null>(null);
  const [reason, setReason] = useState('');
  /**
   * The day being written by hand, if any: whose sheet, which date (null means pick one in the
   * modal), which kind to open on, and the entry being replaced when there is one. One state
   * object rather than four, so a half-cleared modal cannot open on the wrong person's day.
   */
  const [entryTarget, setEntryTarget] = useState<{
    sheet: Timesheet;
    date: string | null;
    kind: 'worked' | 'pto';
    existing?: TimesheetDayEntry;
  } | null>(null);
  /** Set when a hand-entered day changes, cleared by Recalculate: the sheets are behind the record. */
  const [entriesStale, setEntriesStale] = useState(false);

  const periods = useQuery({
    queryKey: listKey('/admin/v1/payroll/periods'),
    queryFn: () => rawApi<Period[]>('GET', '/admin/v1/payroll/periods'),
  });

  /**
   * The fortnight being worked NOW, not the newest one on file.
   *
   * This used to open on `periods.data?.[0]` — the list is newest first, so on a site where the
   * owner has sensibly set up the next two periods in advance, the screen opened on a fortnight
   * nobody has worked yet and reported "No hours worked out for Aug 16 – Aug 31 yet. Choose 'Work
   * out hours'." Seen on the real screen on 29 July: two periods ahead, an instruction to work out
   * hours for it, and the fortnight actually being paid nowhere in sight.
   *
   * So: the period today falls inside, off the ISLAND's calendar rather than the reader's browser
   * — a manager opening this at seven in the evening on the 15th is still on the 15th, and which
   * fortnight that is decides who gets paid. Failing that, the most recent period that has already
   * ended, which is the one somebody is paying if today falls in a gap. Only then the newest row.
   */
  const today = useIslandToday();
  const byStartDesc = [...(periods.data ?? [])].sort((a, b) => b.periodStart.localeCompare(a.periodStart));
  const covering = byStartDesc.find((p) => p.periodStart <= today && today <= p.periodEnd);
  const lastFinished = byStartDesc.find((p) => p.periodEnd < today);
  const activePeriodId = periodId ?? covering?.id ?? lastFinished?.id ?? byStartDesc[0]?.id ?? null;
  const activePeriod = periods.data?.find((p) => p.id === activePeriodId);

  const sheets = useQuery({
    queryKey: listKey(TIMESHEETS_PATH, { payrollPeriodId: activePeriodId ?? '' }),
    queryFn: () => rawApi<Timesheet[]>('GET', `${TIMESHEETS_PATH}?payrollPeriodId=${activePeriodId}`),
    enabled: Boolean(activePeriodId),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: listKey(TIMESHEETS_PATH, { payrollPeriodId: activePeriodId ?? '' }),
    });
  };

  const build = useMutation({
    mutationFn: () => rawApi('POST', `/admin/v1/payroll/periods/${activePeriodId}/timesheets`, {}),
    onSuccess: () => {
      refresh();
      // The rebuild is what carries a hand-entered day into the sheets, so the nag is answered.
      setEntriesStale(false);
      notifyDone('Hours worked out', 'Check each person, then approve.');
    },
    onError: (err) => notifyError(err, 'work out the hours'),
  });

  /**
   * The hand-entered days for whoever is expanded, so a written day can be told from a counted one
   * and offered its Edit and Remove. Queried per person rather than per period because it is read
   * inside one person's opened detail, and the map must never answer with another person's Tuesday.
   */
  const expandedSheet = (sheets.data ?? []).find((r) => r.id === expanded) ?? null;
  const entries = useQuery({
    queryKey: listKey(DAY_ENTRIES_PATH, {
      staffId: expandedSheet?.staffId ?? '',
      from: activePeriod?.periodStart ?? '',
      to: activePeriod?.periodEnd ?? '',
    }),
    queryFn: () =>
      rawApi<TimesheetDayEntry[]>(
        'GET',
        `${DAY_ENTRIES_PATH}?staffId=${expandedSheet?.staffId}&from=${activePeriod?.periodStart}&to=${activePeriod?.periodEnd}`,
      ),
    enabled: Boolean(expandedSheet && activePeriod),
  });
  const entryByDate = new Map((entries.data ?? []).map((e) => [e.date, e]));

  const afterEntryChange = () => {
    void queryClient.invalidateQueries({ queryKey: ['list', DAY_ENTRIES_PATH] });
    setEntriesStale(true);
  };

  const removeEntry = useMutation({
    mutationFn: (id: string) => rawApi('DELETE', `${DAY_ENTRIES_PATH}/${id}`),
    onSuccess: () => {
      notifyDone('Entry removed', 'Recalculate the hours to read the day from the clock again.');
      afterEntryChange();
    },
    onError: (err) => notifyError(err, 'remove this entry'),
  });

  const approve = useMutation({
    mutationFn: (input: { id: string; note?: string }) =>
      rawApi('POST', `${TIMESHEETS_PATH}/${input.id}/approve`, input.note ? { note: input.note } : {}),
    onSuccess: (_d, input) => {
      refresh();
      setSignOff(null);
      const who = rows.find((r) => r.id === input.id)?.staffName ?? 'this person';
      notifyDone('Approved', `${who}'s hours are confirmed.`);
    },
    onError: (err) => notifyError(err, 'approve these hours'),
  });

  const reopen = useMutation({
    mutationFn: (id: string) => rawApi('POST', `${TIMESHEETS_PATH}/${id}/reopen`, {}),
    onSuccess: () => {
      refresh();
      notifyDone('Reopened', 'These hours can be changed again.');
    },
    onError: (err) => notifyError(err, 'reopen these hours'),
  });

  const rows = sheets.data ?? [];
  const pending = rows.filter((r) => r.status !== 'approved');
  const needsAttention = rows.filter((r) => decidingCodes(r.exceptions).length > 0);
  const clearPending = pending.filter((r) => decidingCodes(r.exceptions).length === 0);
  const missingDayLength = rows.some((r) => (r.exceptions ?? []).includes(CONFIGURATION_GAP));
  const noWorkingWeek = rows.filter((r) => (r.exceptions ?? []).includes(NO_WORKING_WEEK));

  return (
    <Paper withBorder p="md" radius="md">
      {/* Wraps on a narrow window. Under `wrap="nowrap"` the period picker could neither shrink
          (it was a fixed 260px) nor drop to a second line, so this one header pushed the whole PAGE
          sideways — 506px of content inside a 390px window, and the only screen in the portal that
          did it. A table that is too wide scrolls inside its own box; a page must not. */}
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={5}>Hours to approve</Title>
          <Text size="sm" c="dimmed">
            What people actually worked, next to what they were scheduled. Approve before building the pay
            register — hourly staff are not paid until you do.
          </Text>
        </div>
        <Group gap="xs" wrap="nowrap">
          <Select
            aria-label="Pay period"
            data={(periods.data ?? []).map((p) => ({
              value: p.id,
              label: `${formatDate(p.periodStart)} – ${formatDate(p.periodEnd)}`,
            }))}
            value={activePeriodId}
            onChange={setPeriodId}
            placeholder="Choose a pay period"
            style={{ flex: '1 1 180px', maxWidth: 260 }}
          />
          {/* The whole fortnight at once, carrying the period being looked at. Correcting one day
              belongs on the day, and it is on every row below — but catching a month up one dialog
              at a time is about eight gestures per person-day, and this is the door that ends that.
              It goes here, beside the period, because that is where somebody is standing when they
              find out the hours are not entered yet. */}
          <Button
            component={Link}
            to={activePeriodId ? periodHoursLink(activePeriodId) : '/payroll?tab=hours'}
            variant="subtle"
            disabled={!activePeriodId}
          >
            Enter the whole period
          </Button>
          <Button
            variant="light"
            loading={build.isPending}
            onClick={() => build.mutate()}
            disabled={!activePeriodId}
          >
            {rows.length ? 'Recalculate' : 'Work out hours'}
          </Button>
        </Group>
      </Group>

      {!activePeriodId ? (
        <Text size="sm" c="dimmed" fs="italic">
          Add a pay period first.
        </Text>
      ) : sheets.isError ? (
        <Alert color="red" title="Could not load hours">
          Nothing has been lost — this is only a display problem.
        </Alert>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No hours worked out for{' '}
          {activePeriod
            ? `${formatDate(activePeriod.periodStart)} – ${formatDate(activePeriod.periodEnd)}`
            : 'this period'}{' '}
          yet. Choose “Work out hours”.
        </Text>
      ) : (
        <>
          {/*
            First, and above the general "some hours need a look", because it is the reason most of
            what is flagged below is flagged at all — and because it is the only thing on this
            screen that keeps costing the same fortnight after fortnight until somebody fixes it
            somewhere else. The link goes straight to the person when there is only one of them.
          */}
          {noWorkingWeek.length > 0 && (
            <NoWorkingWeekAlert sheets={noWorkingWeek} periodStart={activePeriod?.periodStart} />
          )}

          {needsAttention.length > 0 && (
            <Alert color="yellow" variant="light" mb="md" title="Some hours need a look">
              {needsAttention.length} {needsAttention.length === 1 ? 'person has' : 'people have'} something
              worth checking before you approve.
            </Alert>
          )}

          {/* One line about a missing setting, rather than the same red badge on every person. */}
          {missingDayLength && (
            <Alert color="gray" variant="light" mb="md" title="Long days are not being pointed out">
              Set what counts as a normal working day under Working rules &amp; rates, and these hours will be
              checked against it next time they are worked out.
            </Alert>
          )}

          {/* The same nag the adjustments panel gives about its register, for the same reason:
              a day written by hand reaches the sheets when they are next worked out, and reading
              figures that do not include it yet is exactly the confusion this screen must not
              manufacture. */}
          {entriesStale && (
            <Alert color="yellow" variant="light" mb="md">
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm">The hours below do not include the days entered by hand yet.</Text>
                <Button size="compact-sm" loading={build.isPending} onClick={() => build.mutate()}>
                  Recalculate the hours
                </Button>
              </Group>
            </Alert>
          )}

          <Table.ScrollContainer minWidth={820}>
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Person</Table.Th>
                  <Table.Th>Scheduled</Table.Th>
                  <Table.Th>Worked</Table.Th>
                  <Table.Th>Difference</Table.Th>
                  {/*
                    Not the same number as Difference, and the one that costs money. Difference is
                    the fortnight netted off, so four hours over on Saturday and four hours short on
                    Tuesday show as nothing at all; overtime is counted day by day and never nets
                    off, and for anyone whose pay record says their extra hours carry a premium it
                    is what the register prices. It was being computed, stored, approved and paid
                    without ever being shown to the person doing the approving.
                  */}
                  <Table.Th>Overtime</Table.Th>
                  <Table.Th>Needs checking</Table.Th>
                  <Table.Th ta="right">Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((sheet) => {
                  const diff = sheet.workedMinutes - sheet.scheduledMinutes;
                  const isOpen = expanded === sheet.id;
                  const flags = decidingCodes(sheet.exceptions);
                  return (
                    // Keyed on the fragment, not on the two rows inside it: without it React had no
                    // stable identity for a person's pair of rows, so reordering after a rebuild
                    // could carry one person's expanded detail onto another person's row.
                    <Fragment key={sheet.id}>
                      <Table.Tr
                        style={{ cursor: 'pointer' }}
                        onClick={() => setExpanded(isOpen ? null : sheet.id)}
                      >
                        <Table.Td fw={600}>{sheet.staffName ?? 'Unknown'}</Table.Td>
                        <Table.Td>{formatHours(sheet.scheduledMinutes)}</Table.Td>
                        <Table.Td>{formatHours(sheet.workedMinutes)}</Table.Td>
                        <Table.Td>
                          {diff === 0 ? (
                            <Text size="sm" c="dimmed">
                              On schedule
                            </Text>
                          ) : (
                            <Text size="sm" c={diff > 0 ? 'yellow.7' : 'red.7'} fw={600}>
                              {diff > 0 ? '+' : '−'}
                              {formatHours(Math.abs(diff))}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {sheet.overtimeMinutes > 0 ? (
                            <Text size="sm" fw={600}>
                              {formatHours(sheet.overtimeMinutes)}
                            </Text>
                          ) : (
                            <Text size="sm" c="dimmed">
                              None
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          {flags.length === 0 ? (
                            <Text size="sm" c="dimmed">
                              —
                            </Text>
                          ) : (
                            <Group gap={4}>
                              {flags.map((code) => (
                                <Badge key={code} size="sm" variant="light" color={exceptionColor(code)}>
                                  {EXCEPTION_COPY[code] ?? humanize(code)}
                                </Badge>
                              ))}
                            </Group>
                          )}
                        </Table.Td>
                        <Table.Td ta="right">
                          {sheet.status === 'approved' ? (
                            <Stack gap={2} align="flex-end">
                              <Group gap="xs" justify="flex-end" wrap="nowrap">
                                <Badge color="teal" variant="light">
                                  Approved
                                </Badge>
                                <Button
                                  size="compact-sm"
                                  variant="subtle"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    reopen.mutate(sheet.id);
                                  }}
                                >
                                  Reopen
                                </Button>
                              </Group>
                              {/* Who signed these hours off, which is the entire point of asking
                                  somebody to sign them off. The record has always carried the name;
                                  the screen showed a green badge and left the owner to guess which
                                  of his managers had waved a fortnight of overtime through. */}
                              {(sheet.approvedByName || sheet.approvedAt) && (
                                <Text size="xs" c="dimmed">
                                  {sheet.approvedByName ? `by ${sheet.approvedByName}` : 'signed off'}
                                  {sheet.approvedAt ? ` · ${formatDate(sheet.approvedAt)}` : ''}
                                </Text>
                              )}
                              {/* The reason kept with these hours, read back where it was given,
                                  and whole rather than truncated — it is one line, and it is the
                                  answer to the question the register raises weeks later. Named as
                                  being on file rather than left as a bare quote under the approver
                                  above it, because a reason deliberately outlives a reopen: hours
                                  queried once, corrected, then approved in a single click would
                                  otherwise put the first manager's words in the second's mouth. */}
                              {sheet.note && (
                                <Text size="xs" c="dimmed" ta="right" maw={280}>
                                  Reason on file: “{sheet.note}”
                                </Text>
                              )}
                            </Stack>
                          ) : (
                            <Button
                              size="compact-sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                // Hours with nothing flagged stay one click — that is most of the
                                // team, most fortnights, and making a manager type "fine" thirteen
                                // times is how a reason box stops meaning anything. Anything that
                                // needed a decision gets asked what the decision was.
                                if (flags.length === 0) {
                                  approve.mutate({ id: sheet.id });
                                  return;
                                }
                                setReason('');
                                setSignOff(sheet);
                              }}
                              loading={approve.isPending && approve.variables?.id === sheet.id}
                            >
                              Approve
                            </Button>
                          )}
                        </Table.Td>
                      </Table.Tr>
                      <Table.Tr>
                        {/* Spans every column above, so the day-by-day detail stays full width
                            rather than stopping short of the last column it was drawn under. */}
                        <Table.Td colSpan={7} p={0} style={{ border: 0 }}>
                          <Collapse expanded={isOpen}>
                            <Stack gap={4} p="md">
                              {(sheet.days ?? []).length === 0 ? (
                                <Text size="sm" c="dimmed" fs="italic">
                                  No days recorded.
                                </Text>
                              ) : (
                                (sheet.days ?? []).map((day) => {
                                  const entry = entryByDate.get(day.date);
                                  return (
                                    <Group key={day.date} gap="md" wrap="nowrap">
                                      <Text size="sm" style={{ width: 120 }}>
                                        {formatDate(day.date)}
                                      </Text>
                                      <Text size="sm" c="dimmed" style={{ width: 150 }}>
                                        scheduled {formatHours(day.scheduledMinutes)}
                                      </Text>
                                      <Text size="sm" fw={600} style={{ width: 130 }}>
                                        worked {formatHours(day.workedMinutes)}
                                      </Text>
                                      {/* The hand-entered day's own clock times, read back where
                                          the day is judged — the times are the record, and a
                                          record nobody can see is a record nobody can check. */}
                                      {entry?.startTime && entry.endTime && (
                                        <Text size="sm" c="dimmed">
                                          {describeShift(entry.startTime, entry.endTime)}
                                        </Text>
                                      )}
                                      {day.breakMinutes > 0 && (
                                        <Text size="sm" c="dimmed">
                                          less {formatHours(day.breakMinutes)} unpaid break
                                        </Text>
                                      )}
                                      <Group gap={4} ml="auto">
                                        {day.exceptions
                                          .filter((code) => !REDUNDANT_WITH_FIGURES.has(code))
                                          .map((code) => (
                                            <Badge
                                              key={code}
                                              size="xs"
                                              variant="light"
                                              color={exceptionColor(code)}
                                            >
                                              {EXCEPTION_COPY[code] ?? humanize(code)}
                                            </Badge>
                                          ))}
                                      </Group>
                                      {/* Where the clock's answer for a day gets written over by
                                          hand — hours the punches missed, or a paid day off. Only
                                          while the sheet is still open: an approved sheet keeps its
                                          figures until somebody reopens it, and offering an edit
                                          that a rebuild would then ignore is a lie in button form. */}
                                      {isOpen && sheet.status !== 'approved' && (
                                        <Group gap={4} wrap="nowrap">
                                          <Button
                                            size="compact-xs"
                                            variant="subtle"
                                            onClick={() =>
                                              setEntryTarget({
                                                sheet,
                                                date: day.date,
                                                kind: entry?.kind ?? 'worked',
                                                existing: entry,
                                              })
                                            }
                                          >
                                            {entry ? 'Edit' : 'Set hours'}
                                          </Button>
                                          {entry ? (
                                            <ActionIcon
                                              variant="subtle"
                                              color="red"
                                              size="sm"
                                              aria-label="Remove the hand-entered day"
                                              onClick={() => removeEntry.mutate(entry.id)}
                                            >
                                              ✕
                                            </ActionIcon>
                                          ) : (
                                            <Button
                                              size="compact-xs"
                                              variant="subtle"
                                              onClick={() =>
                                                setEntryTarget({ sheet, date: day.date, kind: 'pto' })
                                              }
                                            >
                                              Mark PTO
                                            </Button>
                                          )}
                                        </Group>
                                      )}
                                    </Group>
                                  );
                                })
                              )}
                              <Group gap="xs" mt="xs">
                                {/* The day the sheet has no row for: somebody who never punched
                                    at all has nothing on screen to click, and they are exactly
                                    the person this entry exists for. Opens the same modal with
                                    the date left to pick. */}
                                {isOpen && sheet.status !== 'approved' && (
                                  <Button
                                    size="compact-sm"
                                    variant="subtle"
                                    onClick={() => setEntryTarget({ sheet, date: null, kind: 'worked' })}
                                  >
                                    Enter a day by hand
                                  </Button>
                                )}
                                {/* Where a missed punch actually gets fixed. The flag above says a
                                    shift was never clocked out; this is the only page that can add
                                    the punch, and until it was linked from here the manager had to
                                    go hunting for a screen that asked them which person they
                                    meant. */}
                                <Button
                                  component={Link}
                                  to={`/staff/${sheet.staffId}`}
                                  size="compact-sm"
                                  variant="subtle"
                                >
                                  Open {sheet.staffName ?? 'their record'} to correct a punch
                                </Button>
                                {/* And the same courtesy for the flag that costs the most. Opening
                                    the days of a person nobody has scheduled is where an owner is
                                    standing when he needs this, and the link lands on the editor
                                    with their name already in the title and this period's first day
                                    already in the start date — not on a list, not on a page where
                                    the week is a section between a PIN and a role, and not on a
                                    week that starts too late to change what is on this screen. */}
                                {(sheet.exceptions ?? []).includes(NO_WORKING_WEEK) && (
                                  <Button
                                    component={Link}
                                    to={workingWeekLink(sheet.staffId, activePeriod?.periodStart)}
                                    size="compact-sm"
                                    variant="light"
                                  >
                                    Set {sheet.staffName ? `${sheet.staffName}'s` : 'their'} working week
                                  </Button>
                                )}
                              </Group>
                            </Stack>
                          </Collapse>
                        </Table.Td>
                      </Table.Tr>
                    </Fragment>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          {clearPending.length > 0 && (
            <Group justify="flex-end" mt="md">
              <Button
                variant="light"
                loading={approve.isPending}
                onClick={() => {
                  // Approving the clean ones in one go is the common case; anything flagged still
                  // has to be looked at and approved deliberately.
                  for (const sheet of clearPending) {
                    approve.mutate({ id: sheet.id });
                  }
                }}
              >
                {/* The count is in the label because a disabled button that says "approve everyone"
                    tells you nothing about who that is. */}
                Approve {clearPending.length} {clearPending.length === 1 ? 'person' : 'people'} with nothing
                flagged
              </Button>
            </Group>
          )}
        </>
      )}

      <Modal
        opened={signOff !== null}
        onClose={() => setSignOff(null)}
        title={`Approve ${signOff?.staffName ?? 'these hours'}`}
        closeOnClickOutside={false}
      >
        {signOff && (
          <Stack gap="md">
            <Text size="sm">
              {formatHours(signOff.workedMinutes)} worked against {formatHours(signOff.scheduledMinutes)}{' '}
              scheduled
              {signOff.overtimeMinutes > 0
                ? `, including ${formatHours(signOff.overtimeMinutes)} overtime`
                : ''}
              .
            </Text>
            <Stack gap={4}>
              {decidingCodes(signOff.exceptions).map((code) => (
                <Badge key={code} size="sm" variant="light" color={exceptionColor(code)}>
                  {EXCEPTION_COPY[code] ?? humanize(code)}
                </Badge>
              ))}
            </Stack>
            <Textarea
              label="Why are these hours right?"
              description="Kept with the record, so the register can be explained weeks later"
              autosize
              minRows={2}
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              autoFocus
            />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setSignOff(null)} disabled={approve.isPending}>
                Cancel
              </Button>
              <Button
                onClick={() => approve.mutate({ id: signOff.id, note: reason.trim() })}
                loading={approve.isPending}
                // A blank reason on hours that needed a decision is the state this box exists to
                // stop: the name and the date were already being recorded, and neither of them
                // says why a shift on a day off was paid.
                disabled={reason.trim().length < 3}
              >
                Approve these hours
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {entryTarget && activePeriod && (
        <DayEntryModal
          staffId={entryTarget.sheet.staffId}
          staffName={entryTarget.sheet.staffName ?? 'this person'}
          period={activePeriod}
          initialDate={entryTarget.date}
          initialKind={entryTarget.kind}
          existing={entryTarget.existing}
          onClose={() => setEntryTarget(null)}
          onSaved={() => {
            setEntryTarget(null);
            afterEntryChange();
          }}
        />
      )}
    </Paper>
  );
}
